// UserDashboard.jsx
import React, { useContext, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import styles from "./UserDashboard.module.css";
import { UserContext } from "../../context/UserContext";
import { DataContext } from "../../context/DataContext";
import { phrasesPt, phrasesEn } from "../../data/phrases";
import ActivityModal from "../Library/ActivityModal";
import GoalsCard from "../../components/GoalsCard/GoalsCard";
import { getArchetypeFromTraits } from "../../utils/archetypes27";
import images from "../../assets/personality";
import { computeInsights } from "../../utils/profileHelpers";
import PersonalityTraits from "../../components/Profile/PersonalityTraits";
import { buildPremiumPayload } from "../../services/reportBuilder";
import MetaphorExercisesSection from "../../components/Profile/MetaphorExercisesSection";
import Button from "../../components/common/Button";
import clanTemplatePt from "../../data/pt/temperament_templates.json";
import clanTemplateEn from "../../data/en/temperament_templates.json";

export default function UserDashboard() {
  const { i18n, t } = useTranslation("user");
  const clanTemplate = i18n.language === "pt" ? clanTemplatePt : clanTemplateEn;
  const phrases = i18n.language === "pt" ? phrasesPt : phrasesEn;
  const { userData } = useContext(UserContext);
  const { activitiesList, meetingsList } = useContext(DataContext);
  const navigate = useNavigate();
  const [phrase, setPhrase] = useState("");
  const [suggestedActivity, setSuggestedActivity] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalActivity, setModalActivity] = useState(null);
  const [canViewPremium, setCanViewPremium] = useState(false);

  useEffect(() => {
    setCanViewPremium(userData?.userType?.premiumReport ? true : false);
  }, [userData]);

  const normalizeTraits = (raw) => {
    if (!raw) return [50, 50, 50, 50, 50, 50, 50];
    if (Array.isArray(raw)) {
      const arr = raw.slice(0);
      const nums = arr.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 50;
      });
      while (nums.length < 7) nums.push(50);
      if (nums.length > 7) nums.length = 7;
      return nums;
    }
    // object-like fallback: try numeric keys
    if (typeof raw === "object") {
      const keys = Object.keys(raw)
        .filter((k) => !isNaN(Number(k)))
        .sort((a, b) => a - b);
      if (keys.length > 0) {
        const arr = keys.map((k) => Number(raw[k]));
        return normalizeTraits(arr);
      }
      const maybe = [
        "emotionality",
        "activity",
        "sociability",
        "discipline",
        "mentalOpenness",
        "emotionalOpenness",
        "perspective",
      ]
        .map((k) => raw[k])
        .filter((v) => v !== undefined);
      if (maybe.length >= 3) return normalizeTraits(maybe);
    }
    // string fallback
    if (typeof raw === "string") {
      try {
        return normalizeTraits(JSON.parse(raw));
      } catch (e) {
        return [50, 50, 50, 50, 50, 50, 50];
      }
    }
    return [50, 50, 50, 50, 50, 50, 50];
  };

  const traits = useMemo(
    () => normalizeTraits(userData?.personalityTraits),
    [userData?.personalityTraits]
  );
  // derive archetype (same logic)
  const derivedArchetype = useMemo(() => {
    try {
      const three = traits.slice(0, 3).map((v) => Number(v) || 0);
      if (three.every((n) => n === 0)) return null;
      return getArchetypeFromTraits(three, i18n.language);
    } catch (e) {
      console.warn("derive archetype failed", e);
      return null;
    }
  }, [traits]);

  const clanKey = (derivedArchetype?.meta?.clan || derivedArchetype?.key || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-");
  const clanImg = images[clanKey] || null;

  const insights = useMemo(
    () => computeInsights(traits, derivedArchetype, i18n.language),
    [traits, derivedArchetype]
  );

  const premiumPayload = useMemo(() => {
    try {
      return buildPremiumPayload({
        traitsArray: traits,
        archetypeOverride: derivedArchetype,
        user: userData,
      });
    } catch (e) {
      console.error("buildPremiumPayload failed", e);
      return null;
    }
  }, [traits, derivedArchetype, userData]);

  function getRandomObj(list) {
    if (!Array.isArray(list) || list.length === 0) return null;
    const idx = Math.floor(Math.random() * list.length);
    return list[idx];
  }

  useEffect(() => {
    // chave no localStorage
    const key = "hm_phrase_of_day_v1";
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    try {
      const saved = JSON.parse(localStorage.getItem(key));
      if (saved && saved.date === today && saved.phrase) {
        setPhrase(saved.phrase);
        return;
      }
    } catch (e) {
      // ignore parse errors e continue gerando uma nova frase
    }

    // escolhe aleatoriamente e salva
    const chosen = getRandomObj(phrases);
    setPhrase(chosen || null);
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ date: today, phrase: chosen || null })
      );
    } catch (e) {
      // localStorage pode falhar em ambientes restritos
      // apenas definimos o state e seguimos
    }
  }, []); // roda só na montagem

  useEffect(() => {
    if (!activitiesList) return;

    const key = "hm_daily_suggested_activity_v1";
    const today = new Date().toISOString().slice(0, 10);

    // tentar carregar do localStorage
    const saved = JSON.parse(localStorage.getItem(key));

    if (saved && saved.date === today) {
      setSuggestedActivity(saved.activity);
      return;
    }

    // gerar now
    const { mind = [], move = [], nutri = [], values = [] } = activitiesList;

    const all = [...mind, ...move, ...nutri, ...values];

    if (all.length === 0) {
      setSuggestedActivity(null);
      return;
    }

    // sortear
    const index = Math.floor(Math.random() * all.length);
    const chosen = all[index];

    // incluir o axis se não existir
    const chosenWithAxis = {
      ...chosen,
      axis: mind.includes(chosen)
        ? "mind"
        : move.includes(chosen)
        ? "move"
        : nutri.includes(chosen)
        ? "nutri"
        : values.includes(chosen)
        ? "values"
        : null,
    };

    // salvar no localStorage para persistir até amanhã
    localStorage.setItem(
      key,
      JSON.stringify({ date: today, activity: chosenWithAxis })
    );

    setSuggestedActivity(chosenWithAxis);
  }, [activitiesList]);

  if (!userData) {
    return <div>Carregando...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        <div className={styles.colLeft}>
          {/* <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Encontros</h3>
              <small className={styles.muted}>Acesse suas videochamadas</small>
            </div>

            <div className={styles.meetingsList}>
              {meetingsList.length === 0 ? (
                <>
                  <p className={styles.muted}>Nenhum encontro agendado.</p>
                  <Button className={styles.primaryBtn}>
                    <a target="blank" href="https://thiagorocha.ibhava.com/">
                      Agendar atendimento
                    </a>
                  </Button>
                </>
              ) : (
                meetingsList.map((m) => (
                  <div key={m.id} className={styles.meetingRow}>
                    <div>
                      <div className={styles.meetingTitle}>{m.title}</div>
                      {m.scheduleFor && (
                        <div className={styles.meetingTime}>
                          {new Date(m.scheduleFor).toLocaleDateString("pt-BR", {
                            weekday: "long",
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          • {m.proName || ""}
                        </div>
                      )}
                    </div>
                    <div className={styles.meetingActions}>
                      <Link
                        to={`/meeting/${m.id}`}
                        className={styles.openMeeting}
                      >
                        Detalhes
                      </Link>
                      {m.moderatorOnline && (
                        <a
                          onClick={
                            m.meetingUrl
                              ? () =>
                                  window.open(
                                    m.meetingUrl,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                              : () =>
                                  window.open(
                                    "https://meet.jit.si/${m.jitsiRoomName}",
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                          }
                          target="_blank"
                          rel="noreferrer"
                          className={styles.joinMeeting}
                        >
                          Entrar
                        </a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section> */}

          <section className={styles.card}>
            <header className={styles.headerRow}>
              <h3>{t("profile.title")}</h3>
              {userData?.personalityTraits && canViewPremium ? (
                <Button
                  className="greenButton"
                  onClick={() => navigate("/profile")}
                >
                  {t("userDashboard.buttonProfileFull")}
                </Button>
              ) : (
                <Button
                  className="greenButton"
                  onClick={() => navigate("/profile")}
                >
                  {t("userDashboard.buttonProfile")}
                </Button>
              )}
            </header>
            {userData?.personalityTraits ? (
              <>
                <div className={styles.profileRow}>
                  <div className={styles.clanBox}>
                    <img
                      src={clanImg}
                      alt={
                        clanTemplate?.clans[clanKey]?.label ||
                        derivedArchetype?.label ||
                        "Clã"
                      }
                      className={styles.coverImage}
                    />
                  </div>
                  <div className={styles.profileInfo}>
                    <div className={styles.avatarName}>
                      {userData?.displayName ?? "Usuário"}
                    </div>
                    {/* <small className={styles.muted}>Avatar</small> */}
                    <div className={styles.avatarKey}>
                      <p>{clanTemplate?.clans[clanKey]?.label}</p>
                      <p>"{clanTemplate?.clans[clanKey]?.essence || ""}"</p>
                    </div>
                  </div>
                </div>
                <section className={styles.reportSection}>
                  <MetaphorExercisesSection
                    insights={insights}
                    premiumPayload={premiumPayload}
                    archetypeKey={"navigator"}
                    // archetypeKey={premiumPayload?.archetypeKey}
                    comicsBasePath="./comics/pt"
                    canViewPremium={canViewPremium}
                  />
                </section>
              </>
            ) : (
              <div>
                <small>{t("userDashboard.noPersonalityQuiz")}</small>
                <button
                  style={{ marginTop: "2rem" }}
                  className={styles.primaryBtn}
                  onClick={() => navigate("/personality-quiz")}
                >
                  {t("userDashboard.buttonPersonalityQuiz")}
                </button>
              </div>
            )}
          </section>
        </div>

        {/* Right column: Activity / Tips / Optional widgets */}
        <div className={styles.colRight}>
          {/* <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Atividades recentes</h3>
              <small className={styles.muted}>Progresso e sugestões</small>
            </div>

            <div className={styles.activityList}>
              <div className={styles.activityRow}>
                <div className={styles.activityTitle}>
                  HarmoniaMinds — Streak
                </div>
                <div className={styles.activityMeta}>
                  {userData?.streak?.current ?? 0} dias
                </div>
              </div>
              <div className={styles.activityRow}>
                <div className={styles.activityTitle}>
                  Treino Abertura Mental
                </div>
                <div className={styles.activityMeta}>
                  {userData?.streak?.current ?? 0} dias
                </div>
              </div>
              <div className={styles.activityRow}>
                <div className={styles.activityTitle}>
                  Treino Abertura Emocional
                </div>
                <div className={styles.activityMeta}>
                  {userData?.streak?.current ?? 0} dias
                </div>
              </div>
            </div>
          </section> */}

          <GoalsCard />

          {phrase && (
            <section className={styles.card}>
              <h3>{t("userDashboard.hintDay")}</h3>
              <ul className={styles.tips}>
                <li>{phrase}</li>
              </ul>
            </section>
          )}
          {suggestedActivity && (
            <>
              <section className={styles.card}>
                <h3>{t("userDashboard.activityTitle")}</h3>
                <div
                  className={styles.cardBody}
                  onClick={() => {
                    setModalActivity(suggestedActivity);
                    setModalOpen(true);
                  }}
                >
                  <p>{suggestedActivity.title || "Atividade"}</p>
                  <small>{suggestedActivity.axis?.toUpperCase()}</small>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
      <ActivityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        activity={modalActivity}
      />
    </div>
  );
}
